import React from "react";
import PropTypes from "prop-types";

const BAND_COLOR = {
    pass: "#16a34a",
    warn: "#d98a00",
    fail: "#d64550",
    info: "#2563eb"
};

// Triangle vertices (Ledger anchors the top; GST and Bank/BSA at the base).
const NODES = {
    ledger: { x: 175, y: 46, label: "Ledger", abbr: "L", color: "#16a34a", soft: "#e7f6ee" },
    gst: { x: 56, y: 188, label: "GST", abbr: "G", color: "#2563eb", soft: "#eef3ff" },
    bank: { x: 294, y: 188, label: "Bank / BSA", abbr: "B", color: "#f59e0b", soft: "#fff3e0" }
};

function mid(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function color(band) {
    return BAND_COLOR[band] || BAND_COLOR.info;
}

function Edge({ from, to, label, pct, band, dashed }) {
    const a = NODES[from];
    const b = NODES[to];
    const m = mid(a, b);
    const stroke = color(band);
    return (
        <g>
            <line
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={stroke}
                strokeWidth={dashed ? 1.5 : 2.5}
                strokeDasharray={dashed ? "5 4" : undefined}
                opacity={dashed ? 0.6 : 0.9}
            />
            <g transform={`translate(${m.x - 52}, ${m.y - 18})`}>
                <rect
                    width={104}
                    height={36}
                    rx={9}
                    fill={"#ffffff"}
                    stroke={stroke}
                    strokeWidth={1.2}
                />
                <text x={52} y={14} textAnchor={"middle"} fontSize={9} fill={"#8a93a3"}>
                    {label}
                </text>
                <text
                    x={52}
                    y={28}
                    textAnchor={"middle"}
                    fontSize={13}
                    fontWeight={700}
                    fill={stroke}
                >
                    {pct}%
                </text>
            </g>
        </g>
    );
}

Edge.propTypes = {
    from: PropTypes.string,
    to: PropTypes.string,
    label: PropTypes.string,
    pct: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    band: PropTypes.string,
    dashed: PropTypes.bool
};

function Node({ id }) {
    const n = NODES[id];
    return (
        <g>
            <circle cx={n.x} cy={n.y} r={26} fill={n.soft} stroke={n.color} strokeWidth={2} />
            <text
                x={n.x}
                y={n.y + 6}
                textAnchor={"middle"}
                fontSize={17}
                fontWeight={800}
                fill={n.color}
            >
                {n.abbr}
            </text>
            <text
                x={n.x}
                y={n.y + 44}
                textAnchor={"middle"}
                fontSize={11.5}
                fontWeight={600}
                fill={"#4b5563"}
            >
                {n.label}
            </text>
        </g>
    );
}

Node.propTypes = { id: PropTypes.string };

/**
 * Triangulation correlation graph. Three sources as a triangle; each edge shows
 * what it reconciles and the match %:
 *   GST ↔ Ledger      → Sales
 *   Bank/BSA ↔ Ledger → Repayment
 *   GST ↔ Bank        → Cash coverage (residual, dashed)
 */
const TriangulationDiagram = ({ correlations }) => {
    if (!correlations) {
        return null;
    }
    const { sales, repayment, coverage } = correlations;
    return (
        <div className={"cc-tridiagram"}>
            <svg viewBox={"0 0 350 250"} className={"cc-tridiagram__svg"}>
                {coverage ? (
                    <Edge
                        from={"gst"}
                        to={"bank"}
                        label={"Cash coverage"}
                        pct={coverage.pct}
                        band={coverage.band}
                        dashed
                    />
                ) : null}
                {sales ? (
                    <Edge
                        from={"gst"}
                        to={"ledger"}
                        label={"Sales"}
                        pct={sales.pct}
                        band={sales.band}
                    />
                ) : null}
                {repayment ? (
                    <Edge
                        from={"bank"}
                        to={"ledger"}
                        label={"Repayment"}
                        pct={repayment.pct}
                        band={repayment.band}
                    />
                ) : null}
                <Node id={"ledger"} />
                <Node id={"gst"} />
                <Node id={"bank"} />
            </svg>
            <div className={"cc-tridiagram__legend"}>
                <span>
                    <b>GST ↔ Ledger</b> reconciles sales
                </span>
                <span>
                    <b>Bank/BSA ↔ Ledger</b> reconciles repayment
                </span>
            </div>
        </div>
    );
};

TriangulationDiagram.propTypes = {
    correlations: PropTypes.object
};

export default TriangulationDiagram;
